import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '@/components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { alertsAPI, socAPI } from '@/lib/api';
import { cn, getSeverityColor, formatDate } from '@/lib/utils';
import { AlertTriangle, CheckCircle, Clock, ExternalLink, XCircle, Eye, Filter, User, Target, MessageSquare, Send, RefreshCw, Radio } from 'lucide-react';
import { toast } from 'sonner';
import useAuthStore from '@/store/authStore';

export function AlertsPage() {
  const navigate = useNavigate();
  const { token } = useAuthStore();
  const [alerts, setAlerts] = useState([]);
  const [filteredAlerts, setFilteredAlerts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [severityFilter, setSeverityFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [newNote, setNewNote] = useState('');
  const [sendingNote, setSendingNote] = useState(false);
  const [wsStatus, setWsStatus] = useState('offline');
  const [newCount, setNewCount] = useState(0);
  const wsRef = useRef(null);

  useEffect(() => { loadAlerts(); }, []);
  useEffect(() => { filterAlerts(); }, [alerts, severityFilter, statusFilter]);

  // WebSocket for real-time new alerts
  useEffect(() => {
    if (!token) return;
    const API_URL = process.env.REACT_APP_BACKEND_URL || '';
    const wsUrl = API_URL.replace(/^http/, 'ws') + `/api/ws/alerts?token=${token}`;
    
    const connect = () => {
      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        ws.onopen = () => setWsStatus('live');
        ws.onmessage = (e) => {
          try {
            const msg = JSON.parse(e.data);
            if (msg.type === 'new_alert') {
              setAlerts(prev => [msg.data, ...prev].slice(0, 200));
              setNewCount(c => c + 1);
            }
          } catch (_) {}
        };
        ws.onerror = () => setWsStatus('offline');
        ws.onclose = () => {
          setWsStatus('offline');
          setTimeout(connect, 10000);
        };
      } catch (_) {}
    };
    connect();
    return () => wsRef.current?.close();
  }, [token]);

  const loadAlerts = async () => {
    try {
      const response = await alertsAPI.getAll({ limit: 100 });
      setAlerts(response.data.alerts);
    } catch (error) {
      console.error('Error loading alerts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filterAlerts = () => {
    let filtered = [...alerts];
    if (severityFilter !== 'all') filtered = filtered.filter(a => a.severity === severityFilter);
    if (statusFilter !== 'all') filtered = filtered.filter(a => a.status === statusFilter);
    setFilteredAlerts(filtered);
  };

  const updateAlertStatus = async (alertId, newStatus) => {
    try {
      await socAPI.updateAlert(alertId, { status: newStatus });
      setAlerts(alerts.map(a => a.id === alertId ? { ...a, status: newStatus } : a));
      if (selectedAlert?.id === alertId) setSelectedAlert(prev => ({ ...prev, status: newStatus }));
      toast.success(`Status alterado para ${newStatus}`);
    } catch (error) {
      toast.error('Erro ao atualizar alerta');
    }
  };

  const handleAssume = async (alertId) => {
    try {
      const res = await socAPI.assignAlert(alertId);
      toast.success('Chamado assumido!');
      loadAlerts();
      if (selectedAlert?.id === alertId) {
        setSelectedAlert(prev => ({ ...prev, assigned_to: res.data.assigned_to, status: 'in_progress' }));
      }
    } catch { toast.error('Erro ao assumir chamado'); }
  };

  const handleInvestigate = async (alert) => {
    try {
      await socAPI.investigateAlert(alert.id);
      toast.success('Investigação iniciada — veja na War Room');
      loadAlerts();
      setSelectedAlert(null);
      navigate('/war-room');
    } catch { toast.error('Erro ao iniciar investigação'); }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !selectedAlert) return;
    setSendingNote(true);
    try {
      await socAPI.addNote(selectedAlert.id, newNote);
      toast.success('Nota adicionada');
      setNewNote('');
      loadAlerts();
    } catch { toast.error('Erro ao adicionar nota'); }
    finally { setSendingNote(false); }
  };

  const getSeverityIcon = (severity) => {
    if (severity === 'critical') return <XCircle className="h-4 w-4 text-destructive" />;
    if (severity === 'high') return <AlertTriangle className="h-4 w-4 text-orange-500" />;
    if (severity === 'medium') return <AlertTriangle className="h-4 w-4 text-warning" />;
    return <CheckCircle className="h-4 w-4 text-info" />;
  };

  const stats = {
    critical: alerts.filter(a => a.severity === 'critical' && a.status === 'open').length,
    high: alerts.filter(a => a.severity === 'high' && a.status === 'open').length,
    medium: alerts.filter(a => a.severity === 'medium' && a.status === 'open').length,
    low: alerts.filter(a => a.severity === 'low' && a.status === 'open').length,
  };

  return (
    <div className="min-h-screen" data-testid="alerts-page">
      <Header title="Alerts & Incidents" />
      
      <div className="p-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-destructive/10 border-destructive/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Critical</p>
                  <p className="text-3xl font-mono font-bold text-destructive">{stats.critical}</p>
                </div>
                <XCircle className="h-8 w-8 text-destructive opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-orange-500/10 border-orange-500/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase">High</p>
                  <p className="text-3xl font-mono font-bold text-orange-500">{stats.high}</p>
                </div>
                <AlertTriangle className="h-8 w-8 text-orange-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-warning/10 border-warning/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Medium</p>
                  <p className="text-3xl font-mono font-bold text-warning">{stats.medium}</p>
                </div>
                <AlertTriangle className="h-8 w-8 text-warning opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-info/10 border-info/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Low</p>
                  <p className="text-3xl font-mono font-bold text-info">{stats.low}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-info opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="bg-card/50 border-border">
          <CardContent className="pt-6">
            <div className="flex gap-4">
              <Select value={severityFilter} onValueChange={setSeverityFilter}>
                <SelectTrigger className="w-[150px]" data-testid="severity-filter">
                  <SelectValue placeholder="Severidade" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px]" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="investigating">Investigating</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="false_positive">False Positive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="bg-card/50 border-border">
          <CardHeader>
            <CardTitle className="font-display text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              Alertas ({filteredAlerts.length})
              <div className="flex items-center gap-1.5 ml-auto">
                <span className={cn("h-1.5 w-1.5 rounded-full", wsStatus === 'live' ? 'bg-emerald-400 animate-pulse' : 'bg-gray-500')} />
                <span className="text-[10px] font-mono normal-case">{wsStatus === 'live' ? 'LIVE' : 'offline'}</span>
                {newCount > 0 && <Badge className="bg-red-500/20 text-red-300 border-red-500/30 text-[10px] px-1.5 h-4">+{newCount} novos</Badge>}
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[500px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Severidade</TableHead>
                    <TableHead>Título</TableHead>
                    <TableHead>Fonte</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Origem</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAlerts.map((alert) => (
                    <TableRow key={alert.id} className="hover:bg-secondary/50">
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getSeverityIcon(alert.severity)}
                          <Badge className={cn("border", getSeverityColor(alert.severity))}>
                            {alert.severity.toUpperCase()}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{alert.title}</p>
                          <p className="text-xs text-muted-foreground line-clamp-1">{alert.description}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="font-mono text-xs">
                          {alert.source.toUpperCase()}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={alert.status === 'resolved' ? 'default' : 'secondary'}>
                          {alert.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">{alert.source_country || '-'}</TableCell>
                      <TableCell className="text-sm text-muted-foreground font-mono">
                        {formatDate(alert.created_at)}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => setSelectedAlert(alert)}
                            data-testid={`view-alert-${alert.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {alert.status === 'open' && (
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => updateAlertStatus(alert.id, 'investigating')}
                            >
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Alert Detail Dialog */}
      <Dialog open={!!selectedAlert} onOpenChange={() => setSelectedAlert(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedAlert && getSeverityIcon(selectedAlert.severity)}
              {selectedAlert?.title}
            </DialogTitle>
            <DialogDescription>{selectedAlert?.description}</DialogDescription>
          </DialogHeader>
          {selectedAlert && (
            <Tabs defaultValue="details">
              <TabsList className="bg-secondary">
                <TabsTrigger value="details">Detalhes</TabsTrigger>
                <TabsTrigger value="notes">
                  Notas ({(selectedAlert.case_notes || []).length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="details" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Severidade</p>
                    <Badge className={cn("mt-1 border", getSeverityColor(selectedAlert.severity))}>
                      {selectedAlert.severity?.toUpperCase()}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Status</p>
                    <Badge className="mt-1" variant="secondary">{selectedAlert.status}</Badge>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">IP de Origem</p>
                    <p className="font-mono mt-1">{selectedAlert.source_ip || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">País</p>
                    <p className="font-mono mt-1">{selectedAlert.source_country || '-'}</p>
                  </div>
                  {selectedAlert.mitre_technique && (
                    <div>
                      <p className="text-xs text-muted-foreground uppercase">MITRE ATT&CK</p>
                      <Badge variant="outline" className="mt-1 font-mono">{selectedAlert.mitre_technique}</Badge>
                    </div>
                  )}
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Criado em</p>
                    <p className="font-mono mt-1 text-sm">{formatDate(selectedAlert.created_at)}</p>
                  </div>
                  {selectedAlert.assigned_to && (
                    <div className="col-span-2">
                      <p className="text-xs text-muted-foreground uppercase">Responsável</p>
                      <div className="flex items-center gap-2 mt-1">
                        <User className="h-4 w-4 text-primary" />
                        <p className="font-medium">{selectedAlert.assigned_to}</p>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex gap-2 pt-4 border-t border-border flex-wrap">
                  {!selectedAlert.assigned_to && (
                    <Button size="sm" variant="outline" onClick={() => handleAssume(selectedAlert.id)}>
                      <User className="h-4 w-4 mr-1" />Assumir
                    </Button>
                  )}
                  <Button
                    size="sm"
                    className="bg-warning/20 text-warning border border-warning/30 hover:bg-warning/30"
                    onClick={() => handleInvestigate(selectedAlert)}
                    disabled={selectedAlert.in_war_room}
                    data-testid="investigate-btn"
                  >
                    <Target className="h-4 w-4 mr-1" />
                    {selectedAlert.in_war_room ? 'Em Investigação' : 'Investigar (War Room)'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => updateAlertStatus(selectedAlert.id, 'resolved')}
                    disabled={selectedAlert.status === 'resolved'}
                  >
                    <CheckCircle className="h-4 w-4 mr-1" />Resolver
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => updateAlertStatus(selectedAlert.id, 'false_positive')}
                  >
                    Falso Positivo
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="notes" className="mt-4 space-y-3">
                <ScrollArea className="h-40">
                  {(selectedAlert.case_notes || []).length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">Nenhuma nota ainda</p>
                  ) : (
                    (selectedAlert.case_notes || []).map((note) => (
                      <div key={note.id} className="mb-3 p-3 bg-secondary/50 rounded border border-border">
                        <div className="flex items-center gap-2 mb-1">
                          <User className="h-3 w-3" />
                          <span className="text-xs font-medium">{note.user}</span>
                          <span className="text-xs text-muted-foreground ml-auto">{note.timestamp?.substring(0,16)?.replace('T',' ')}</span>
                        </div>
                        <p className="text-sm">{note.note}</p>
                      </div>
                    ))
                  )}
                </ScrollArea>
                <div className="flex gap-2">
                  <Textarea
                    placeholder="Adicionar nota ao caso..."
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    className="flex-1 min-h-[70px] resize-none text-sm"
                    data-testid="note-input"
                    onKeyDown={(e) => { if (e.key === 'Enter' && e.ctrlKey) handleAddNote(); }}
                  />
                  <Button size="sm" className="self-end" onClick={handleAddNote} disabled={sendingNote || !newNote.trim()} data-testid="add-note-btn">
                    {sendingNote ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </Button>
                </div>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AlertsPage;
